// [15b 20260815 Steven令] 自动化交易 · 常驻两线盈亏条
//
// 口径 C（Steven 20260814 拍板）：**日线两线分列不合计，仅本月合计**。
//   T+0：当天开当天平 ⇒ 盈亏日 = 交易日，天天有数
//   波段：按 swing_real_rounds.exitDate 结算且 holdDays>1 ⇒ 多数日子 0 平仓
//   若日线强行加总，会在"波段持仓浮盈几千但当日无平仓"时宣称合计仅为 T+0 数额，
//   等于声称波段当天没有贡献 —— 与事实相反。故本组件**任何位置都不出现"今日合计"**。
//   月度层面 holdDays 造成的归属漂移被时间抹平，合计才成立（且仅合计实盘，模拟单列）。
//
// 常驻语义：本条在 T+0 / 波段两种视图下都渲染，切线不消失 —— 满足 Steven
//   「方便**同时**看每天的盈利数据」的原始诉求（做成标签页则看一条时看不到另一条）。
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, Bot, Waves } from "lucide-react";
import { useMemo } from "react";

const yuan = (v: number) => (v >= 0 ? "+" : "") + Math.round(v).toLocaleString("zh-CN");
const tone = (v: number) =>
  v > 0 ? "text-red-500" : v < 0 ? "text-green-500" : "text-muted-foreground";

export default function TradingOverviewStrip() {
  const { data } = trpc.tradingOverview.summary.useQuery(undefined, { refetchInterval: 30000 });
  const codes = useMemo(
    () => (data?.swing.positions ?? []).map((p: { code: string }) => p.code),
    [data]
  );
  const { data: quotes } = trpc.swingTrade.quotes.useQuery(
    { codes },
    { enabled: codes.length > 0, refetchInterval: 30000 }
  );

  // 波段持仓浮动：需实时价，取不到的持仓不计入并标注，绝不用成本价冒充现价
  const float = useMemo(() => {
    let pnl = 0;
    let priced = 0;
    for (const p of data?.swing.positions ?? []) {
      const last = (quotes as any)?.[p.code]?.last ?? 0;
      if (last > 0) {
        pnl += (last - p.avgPx) * p.qty;
        priced++;
      }
    }
    return { pnl, priced, total: (data?.swing.positions ?? []).length };
  }, [data, quotes]);

  if (!data) return null;
  const t0 = data.t0;
  const sw = data.swing;
  const swToday = sw.todayClosedReal.pnl + sw.todayClosedSim.pnl;
  const swTodayN = sw.todayClosedReal.n + sw.todayClosedSim.n;

  return (
    <div className="rounded-lg border bg-card/60 px-3 py-2 text-xs space-y-1.5">
      {/* ── 今日：两线分列，刻意不给合计 ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="flex items-center gap-1 font-semibold text-foreground shrink-0">
          今日
        </span>

        <span className="flex items-center gap-1.5">
          <Bot className="h-3.5 w-3.5 text-primary" />
          <span className="text-muted-foreground">T+0</span>
          <span className={`font-mono font-semibold ${tone(t0.todayLive)}`}>
            实盘 {yuan(t0.todayLive)}
          </span>
          <span className={`font-mono ${tone(t0.todaySim)}`}>模拟 {yuan(t0.todaySim)}</span>
          <span className="text-muted-foreground">
            {t0.todayLegs}笔 · {t0.liveCount}只实盘
          </span>
        </span>

        <span className="flex items-center gap-1.5">
          <Waves className="h-3.5 w-3.5 text-sky-500" />
          <span className="text-muted-foreground">波段</span>
          <span className={`font-mono ${tone(swToday)}`}>
            平仓 {swTodayN}笔 {yuan(swToday)}
          </span>
          {float.total > 0 && (
            <span className={`font-mono ${tone(float.pnl)}`}>
              持仓{float.total}只 浮动 {yuan(float.pnl)}
              {float.priced < float.total && (
                <span className="text-muted-foreground">（{float.total - float.priced}只无报价未计）</span>
              )}
            </span>
          )}
          {sw.todayClosedReal.n === 0 && sw.monthClosedReal.n === 0 && (
            <Badge variant="outline" className="h-4 px-1 text-[10px]">模拟</Badge>
          )}
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help text-muted-foreground">
              <Info className="h-3 w-3 inline" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-sm text-xs">
            <p className="font-semibold">为什么今日不给"合计"？</p>
            <p className="mt-1">
              T+0 当天开当天平，盈亏日就是交易日；波段按平仓日（exitDate）结算，持仓天数常大于 1，
              多数交易日没有平仓。两者口径不同，<b>日线相加会在波段有浮盈但未平仓时，
              误报成"波段今天没有贡献"</b>。
            </p>
            <p className="mt-1">本月两线归属漂移被时间抹平，故仅本月给合计（且只合计实盘）。</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* ── 本月：口径已可合计（仅实盘） ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-1.5">
        <span className="font-semibold text-foreground shrink-0">本月</span>
        <span className="text-muted-foreground">
          T+0 实盘 <span className={`font-mono ${tone(t0.monthLive)}`}>{yuan(t0.monthLive)}</span>
        </span>
        <span className="text-muted-foreground">
          波段实盘{" "}
          <span className={`font-mono ${tone(sw.monthClosedReal.pnl)}`}>
            {yuan(sw.monthClosedReal.pnl)}
          </span>
        </span>
        <span className="font-semibold">
          合计{" "}
          <span className={`font-mono ${tone(data.monthTotalLive)}`}>
            {yuan(data.monthTotalLive)}
          </span>
        </span>
        <span className="text-muted-foreground/70">
          （模拟 T+0 {yuan(t0.monthSim)} · 波段 {yuan(sw.monthClosedSim.pnl)}）
        </span>
      </div>
    </div>
  );
}
